import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Film, 
  ArrowRight, 
  Calendar,
  Globe,
  TrendingUp,
  Users,
  DollarSign,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Mail,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductDemo from '../components/ProductDemo';
import { AnimatedCounter, FloatingParticles } from '../components/AnimatedEffects';

const Investors = () => {
  const phases = [
    {
      phase: '1',
      title: 'Script Platform',
      status: 'Live Now',
      description: 'AI-guided screenplay writing with the 3-act structure. Users stay the author; AI handles the craft.',
      color: 'bg-primary',
      active: true
    },
    {
      phase: '2',
      title: 'AI Video Production',
      status: 'Year 1-2',
      description: 'Turn completed scripts into AI-generated short films. New revenue stream, same customer base.',
      color: 'bg-blue-500',
      active: false
    },
    {
      phase: '3',
      title: 'Film Competitions',
      status: 'Year 2-3',
      description: 'MovieBot-hosted competitions with cash prizes. Community flywheel + content pipeline.',
      color: 'bg-purple-500',
      active: false
    },
    {
      phase: '4',
      title: 'Streaming Partnerships',
      status: 'Year 3+',
      description: 'New content category: "Human-made, AI-generated films." Tubi, Netflix, Hulu distribution.',
      color: 'bg-emerald-500',
      active: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Minimal Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-black text-lg tracking-tight">MOVIEBOT</span>
          </Link>
          <div className="flex items-center gap-4">
            <a 
              href="https://moviebot.tech" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              Try Product
            </a>
            <a 
              href="mailto:Admin@moviebot.net?subject=MovieBot Investment Inquiry"
              className="text-sm bg-white text-black px-4 py-2 font-medium hover:bg-white/90 transition-colors"
            >
              Contact Founder
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(245,158,11,0.1)_0%,_transparent_50%)]" />
        
        {/* Floating particles */}
        <FloatingParticles count={20} color="primary" />
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-primary font-mono text-sm tracking-wider mb-6">FOR INVESTORS</p>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-8">
                The platform that turns<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-amber-400 to-orange-500">
                  movie ideas into movies.
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed mb-10">
                Today: AI-guided screenwriting. Tomorrow: video production, competitions, and a new category of streaming content. We're building the creative infrastructure for the AI filmmaking era.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://moviebot.tech"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-white text-black hover:bg-white/90 font-bold text-lg px-8 py-6 w-full sm:w-auto">
                    <Play className="w-5 h-5 mr-2" />
                    Try the Product
                  </Button>
                </a>
                <a 
                  href="mailto:Admin@moviebot.net?subject=MovieBot Investment Inquiry"
                >
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-bold text-lg px-8 py-6 w-full sm:w-auto">
                    <Mail className="w-5 h-5 mr-2" />
                    Email Founder
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Right - Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-primary/20">
                <img 
                  src="https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800&q=80"
                  alt="Cinema Industry"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                
                {/* Stats overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-black/60 backdrop-blur-sm border border-white/10 p-3 text-center">
                      <p className="text-2xl font-black text-primary">$17B+</p>
                      <p className="text-[10px] text-muted-foreground">Hollywood dev spend</p>
                    </div>
                    <div className="bg-black/60 backdrop-blur-sm border border-white/10 p-3 text-center">
                      <p className="text-2xl font-black text-primary">186</p>
                      <p className="text-[10px] text-muted-foreground">Countries reached</p>
                    </div>
                    <div className="bg-black/60 backdrop-blur-sm border border-white/10 p-3 text-center">
                      <p className="text-2xl font-black text-primary">5.8%</p>
                      <p className="text-[10px] text-muted-foreground">Conversion rate</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 border-2 border-primary/30 rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary rounded-full blur-xl opacity-30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Opportunity */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-8">THE OPPORTUNITY</h2>
            
            <div className="space-y-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
              <p>
                Hollywood spends <span className="text-white font-semibold">$17B+ annually</span> on content development. Millions of people have movie ideas stuck in their heads with no way to get them onto paper in proper format. The tools that exist today—Final Draft, Celtx—present a blank page and assume you already know how to write.
              </p>
              
              <p>
                The AI content creation wave is real, but it's fragmented. ChatGPT can help with dialogue but doesn't understand screenplay structure. Image generators can create frames but can't tell a story. <span className="text-white font-semibold">MovieBot is the guided, structured entry point</span>—the platform that meets users where they are (an idea) and gets them to where they want to be (a finished script).
              </p>
              
              <p>
                We're not competing with professional screenwriting software. We're creating an entirely new category: <span className="text-white font-semibold">AI-native storytelling for everyone.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Validated Traction */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-4">VALIDATED TRACTION</h2>
            <p className="text-2xl text-white font-semibold mb-16 max-w-xl">
              Early signals that matter. All organic, pre-launch.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
              {[
                { value: 179, label: 'Paying customers', sublabel: 'Pre-launch', suffix: '' },
                { value: 2894, label: 'Early revenue', sublabel: 'Manually fulfilled', prefix: '$' },
                { value: 6, label: 'Sales lift', sublabel: '$10 → $14.99 price test', suffix: 'x' },
                { value: 3100, label: 'Email signups', sublabel: 'Zero ad spend', suffix: '' },
                { value: 29143, label: 'Unique visitors', sublabel: '186 countries', suffix: '' },
                { value: 39, label: 'Traffic from India', sublabel: 'Massive TAM', suffix: '%' },
                { value: 5.8, label: 'Signup → Paid', sublabel: '2x industry avg', suffix: '%', decimals: true },
                { value: 19.99, label: 'First subscriber tier', sublabel: 'Pro plan', prefix: '$', decimals: true },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05, duration: 0.4 }}
                  className="bg-white/[0.02] border border-white/5 p-6 md:p-8 hover:bg-white/[0.04] transition-colors group"
                >
                  <p className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">
                    {stat.prefix || ''}
                    {stat.decimals ? (
                      stat.value
                    ) : (
                      <AnimatedCounter end={stat.value} duration={2} />
                    )}
                    {stat.suffix || ''}
                  </p>
                  <p className="text-sm md:text-base text-white font-medium mb-1 group-hover:text-primary transition-colors">
                    {stat.label}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {stat.sublabel}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Product */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-8">THE PRODUCT</h2>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                  User stays the author.<br />
                  AI handles the craft.
                </h3>
                
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    MovieBot guides users through the <span className="text-white">proven 3-act structure</span> used by every Hollywood film. At each story beat, the AI generates options—the user picks the direction that feels right for their story.
                  </p>
                  <p>
                    It's not ChatGPT (generic, unstructured). It's not Final Draft (blank page, intimidating). It's the middle ground: <span className="text-white">guided creativity that produces industry-standard output.</span>
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>Culturally-aware AI (Hollywood, Bollywood, Nollywood styles)</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>Pro Scene Studio for power users</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>Act 1 free, full script requires subscription</span>
                  </div>
                </div>
              </div>

              {/* Animated Product Demo */}
              <div className="relative">
                <ProductDemo />
                
                <div className="absolute -bottom-4 -right-4 bg-primary/20 border border-primary/30 px-4 py-2 text-sm font-mono">
                  Live Demo
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Vision - 4 Phases */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-4">THE VISION</h2>
            <p className="text-2xl text-white font-semibold mb-16 max-w-xl">
              Script → Video → Competitions → Streaming
            </p>

            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-primary via-blue-500 via-purple-500 to-emerald-500 opacity-30" />
              
              <div className="grid md:grid-cols-4 gap-6">
                {phases.map((phase, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1, duration: 0.4 }}
                    className={`relative ${phase.active ? 'bg-white/[0.04]' : 'bg-white/[0.02]'} border ${phase.active ? 'border-primary/30' : 'border-white/5'} p-6`}
                  >
                    {/* Phase indicator */}
                    <div className={`w-8 h-8 ${phase.color} flex items-center justify-center text-sm font-bold text-white mb-4 ${phase.active ? '' : 'opacity-50'}`}>
                      {phase.phase}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-lg font-bold text-white">{phase.title}</h3>
                      {phase.active && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 font-medium">LIVE</span>
                      )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground font-mono mb-3">{phase.status}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{phase.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Current Moment */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-8">CURRENT MOMENT</h2>
            
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 p-8 md:p-12">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-2 mb-6">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-amber-400 font-mono text-sm">LIVE NOW</span>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Summer Film Competition
              </h3>
              
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Our flagship competition is live, driving user acquisition and stress-testing the platform at scale. Entries coming in daily from 180+ countries. Winner gets full short film production and streaming submission.
              </p>

              <Link to="/challenge">
                <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  View Challenge Details
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Try It Yourself */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Don't just read about it.
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Write a scene in under 10 minutes. Act 1 is free—no credit card required.
            </p>
            
            <a 
              href="https://moviebot.tech"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-10 py-6">
                Start Writing Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Founder Contact */}
      <section className="py-24 px-6 bg-white/[0.02] border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-sm font-mono text-primary tracking-wider mb-8">LET'S TALK</h2>
            
            <div className="mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-amber-500/20 rounded-full mx-auto mb-6 flex items-center justify-center text-3xl font-bold text-white">
                AF
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Amir Foster</h3>
              <p className="text-muted-foreground">Founder & CEO, MovieBot</p>
            </div>

            <p className="text-lg text-muted-foreground mb-10 max-w-lg mx-auto">
              I'm happy to walk through our metrics, roadmap, and what we're building. If you're interested in the AI + entertainment intersection, let's connect.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:Admin@moviebot.net?subject=MovieBot Investment Inquiry">
                <Button className="bg-white text-black hover:bg-white/90 font-bold px-8 py-6 w-full sm:w-auto">
                  <Mail className="w-5 h-5 mr-2" />
                  Admin@moviebot.net
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-primary" />
            <span className="font-bold">MOVIEBOT</span>
            <span className="text-muted-foreground text-sm ml-2">© 2026</span>
          </div>
          <p className="text-sm text-muted-foreground">
            This page is confidential and intended for invited investors only.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Investors;
